import { LoadingSpinner } from '@carlito/dashboard-ui';
import { Switch } from '@headlessui/react';
import classnames from 'classnames';
import React, { ReactNode } from 'react';
import { IconType } from 'react-icons';
import cn from 'classnames';

export type LabelledFormElementBadgeColor = 'red' | 'grey' | 'green' | 'yellow';

export interface LabelledFormElementProps {
  label: ReactNode;
  className?: string;
  loading?: boolean;
  labelClassName?: string;
  noShadow?: boolean;
  /**
   * If defined, a toggle besides
   * the label will be rendered.
   * It enables/disables the form element
   */
  disableToggle?: {
    onToggle: (isDisabled: boolean) => void;
    isToggled: boolean;
    label: string;
    /**
     * If true, the toggle operates in a reverse manner,
     * meaning that:
     * Toggled Form Elemen = Enabled
     * Untoggled Form Element = Disabled
     */
    reverse?: boolean;
  };
  actions?: {
    Icon: IconType;
    onClick: () => void;
    name: string;
    loading?: boolean;
  }[];
  badge?: {
    color: LabelledFormElementBadgeColor;
  };
}

export const LabelledFormElement: React.FC<LabelledFormElementProps> = ({
  label,
  children,
  className = 'sm:col-span-4',
  labelClassName,
  loading,
  noShadow,
  disableToggle,
  actions = [],
  badge,
}) => {
  const isUiGreyedOut = !!(disableToggle?.reverse
    ? !disableToggle?.isToggled
    : disableToggle?.isToggled);

  const isUiToggled = !!disableToggle?.isToggled;

  return (
    <div
      className={classnames('relative', className, {
        'pt-2': disableToggle,
      })}
    >
      {loading && <div className="skeleton-loading z-10 w-full h-full absolute top-0 left-0" />}
      <label className={cn('block text-sm font-medium text-gray-700', labelClassName)}>
        {label}
        {badge && (
          <span
            className={classnames('inline-block h-2 w-2 ml-2 rounded-full', {
              'bg-red-500': badge.color === 'red',
              'bg-green-500': badge.color === 'green',
              'bg-gray-500': badge.color === 'grey',
              'bg-yellow-500': badge.color === 'yellow',
            })}
          />
        )}
      </label>
      {(disableToggle || actions.length > 0) && (
        <div className="absolute right-0 -top-2 flex gap-2">
          {actions.map((action) => (
            <button type="button" key={action.name} onClick={action.onClick}>
              {action.loading ? (
                <LoadingSpinner />
              ) : (
                <action.Icon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
              )}
            </button>
          ))}
          {disableToggle && (
            <Switch.Group as="div" className="flex items-end gap-2">
              <Switch.Label className="text-sm font-medium" as="span">
                {disableToggle.label}
              </Switch.Label>
              <Switch
                checked={isUiToggled}
                onChange={() => {
                  disableToggle.onToggle(isUiToggled);
                }}
                className={classnames(
                  isUiToggled ? 'bg-secondary-default' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-default'
                )}
              >
                <span
                  aria-hidden="true"
                  className={classnames(
                    isUiToggled ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200'
                  )}
                />
              </Switch>
            </Switch.Group>
          )}
        </div>
      )}
      <div
        className={classnames('mt-1 flex rounded-md w-full', {
          'shadow-sm': !noShadow,
          'opacity-30 pointer-events-none': isUiGreyedOut,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default LabelledFormElement;
